import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { TableKit } from "@tiptap/extension-table"

// Rich-text (TipTap) editor for Knowledge Base pages. Mounts onto an empty
// div, seeds itself from the hidden <input> (sanitized HTML) and writes the
// editor's HTML back to that input on every change so a normal form submit
// carries the content. The server re-sanitizes on save (KnowledgeContent),
// so the editor never needs to be trusted.
export default class extends Controller {
  static targets = ["editor", "input", "toolbar"]

  // Commands handled by the toolbar buttons (data-cmd) that have an active
  // state worth reflecting in the UI.
  static ACTIVE_MARKS = [
    "bold", "italic", "strike", "code",
    "bulletList", "orderedList", "blockquote", "codeBlock", "link",
  ]

  connect() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          link: {
            openOnClick: false,
            autolink: true,
            HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
          },
        }),
        TableKit.configure({ table: { resizable: true } }),
      ],
      content: this.inputTarget.value || "",
      onUpdate: ({ editor }) => this.sync(editor),
      onSelectionUpdate: () => this.refreshToolbar(),
      onTransaction: () => this.refreshToolbar(),
    })
    this.refreshToolbar()
  }

  disconnect() {
    this.editor?.destroy()
    this.editor = null
  }

  sync(editor) {
    this.inputTarget.value = editor.isEmpty ? "" : editor.getHTML()
  }

  cmd(event) {
    event.preventDefault()
    const { cmd, level } = event.currentTarget.dataset
    const chain = this.editor.chain().focus()

    switch (cmd) {
      case "bold":        chain.toggleBold().run(); break
      case "italic":      chain.toggleItalic().run(); break
      case "strike":      chain.toggleStrike().run(); break
      case "code":        chain.toggleCode().run(); break
      case "heading":     chain.toggleHeading({ level: Number(level) }).run(); break
      case "paragraph":   chain.setParagraph().run(); break
      case "bulletList":  chain.toggleBulletList().run(); break
      case "orderedList": chain.toggleOrderedList().run(); break
      case "blockquote":  chain.toggleBlockquote().run(); break
      case "codeBlock":   chain.toggleCodeBlock().run(); break
      case "hr":          chain.setHorizontalRule().run(); break
      case "table":       chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); break
      case "link":        this.setLink(); break
      case "undo":        chain.undo().run(); break
      case "redo":        chain.redo().run(); break
    }
  }

  setLink() {
    const previous = this.editor.getAttributes("link").href
    const url = window.prompt("Link URL", previous || "https://")
    if (url === null) return // cancelled

    const chain = this.editor.chain().focus().extendMarkRange("link")
    if (url === "") chain.unsetLink().run()
    else chain.setLink({ href: url }).run()
  }

  refreshToolbar() {
    if (!this.hasToolbarTarget || !this.editor) return

    this.toolbarTarget.querySelectorAll("[data-cmd]").forEach((btn) => {
      const { cmd, level } = btn.dataset
      let active = false
      try {
        if (cmd === "heading") active = this.editor.isActive("heading", { level: Number(level) })
        else if (this.constructor.ACTIVE_MARKS.includes(cmd)) active = this.editor.isActive(cmd)
      } catch (_) { /* command not registered */ }
      btn.classList.toggle("is-active", active)
    })
  }
}
